import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { FileText, Loader2 } from "lucide-react";
import type { AiProposal } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, ChatPanel, PromptInputAttachment, PromptInputAttachments, SourceDrawer, toast, type AgentStepLike, type ChatMessageLike, type CitationLike, type InlineWidgetRef, notifySaved, notifySaveFailed } from "@ncp/ui";
import { assistantKeys, useAssistantStream, useAttach, useDecideConfirmation, useInbox, useMessageFeedback, useThread, useThreadRealtime, type LiveTurn, type ThreadStepPayload } from "./hooks";
import { ChatEmptyState, type StartMode, type StarterPrompt } from "./chat/EmptyState";
import { CompletionMenu, ComposerTools, ModeChip, directiveFor, useCompletion, useSpeechInput, type CompletionItem, type ComposerMode, type PlaybookOption } from "./chat/Composer";
import { AnswerFooter, ConfirmCard, ConfirmationResult, LiveExtras, RunProgress, SystemNote, traceOf } from "./chat/Turn";
import { LiveLine, LiveSteps, TurnSteps } from "./chat/WorkSteps";
import { AnswerKeys, answerLayout, entryKeys, isInlineLive, isInlineMessage, questionKey } from "./inlineLayout";
import { AskUserCard } from "./chat/AskUserCard";
import { InspectorChat, type InspectorEntry } from "./chat/Inspector";
import { OpCard, OpConfirmCard } from "./chat/OpCard";
import { useAgentPresence, type Presence } from "./presence";
import type { AnswerBlock, AskUserEvent, AssistantSummary, ClientToolCall, KnowledgeSourceKey, MessageCitation, MessageContext, OpenPage, TurnSourceChoice, SuggestedPrompt, SurfaceFocus, SurfaceSeen, SurfaceState, ThreadMessage, ToolConfirmation, UiEvent, UploadedAttachment } from "./types";

/** What the ECONIC sidebar puts around the inspector conversation (DESIGN-SYSTEM §10.15): route line, segmented controls, note, chips. */
export interface InspectorSlots {
  routeLine?: ReactNode;
  controls?: ReactNode;
  note?: ReactNode;
  /** Starter chips above the composer (shown while the conversation is empty, or the agent's follow-ups). */
  chips?: string[];
  placeholder?: string;
  /** Replaces the empty conversation's starter cards. */
  emptyState?: ReactNode;
}

/**
 * What a host-drawn conversation (`chrome="custom"`, the ECONIC Agent full screen) receives: the transcript entries the panel built (the
 * same as the inspector's: answers with their work steps, blocks, op cards, questions, confirmations and footers), the composer's state and
 * the actions to send, stop, attach and open a citation. The host lays them out; the panel keeps the stream, the thread and the drawer.
 */
export interface PanelChromeProps {
  entries: InspectorEntry[];
  /** A turn is streaming or a file is uploading. */
  busy: boolean;
  streaming: boolean;
  /** The thread is loading (no entries yet). */
  loading: boolean;
  live: LiveTurn | null;
  draft: string;
  setDraft: (text: string | ((current: string) => string)) => void;
  /** Sends the text (default: the draft) with the host's directive and `directive`; `research` sends it to the evidence researcher. */
  /** `replaces`: "Regenerate" / "Edit and resend" — the id of the thread's last question, which this message replaces with its answer. */
  send: (text?: string, options?: { research?: boolean; directive?: string | null; replaces?: string | null }) => void;
  stop: () => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Handles the composer's keys for the @specialist and /process completion; true when it took the key. */
  onComposerKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** The completion menu while "@" or "/" is being typed, else null. */
  completion: ReactNode;
  /** Uploads files to go with the next message; undefined when the assistant takes none (or there is no thread yet). */
  attach?: (files: File[]) => void;
  accept: string;
  pending: { files: UploadedAttachment[]; uploading: string[]; remove: (documentId: string) => void };
  openCitation: (c: CitationLike) => void;
  assistantName: string;
  assistant: AssistantSummary | null;
  /** Dictation in the reader's language, when the browser offers speech recognition. */
  speech: { supported: boolean; listening: boolean; toggle: () => void } | null;
  /** What the agent is doing (presence.ts), for the host's faces. */
  presence: Presence;
}

/** A host request: sent once per `id`. `focus` is what "✦ Dive in" points at; `directive` steers the turn; `scope` labels the bubble. */
export interface PanelRequest { id: string; text: string; focus?: SurfaceFocus | null; directive?: string | null; scope?: "department" | null }

/** Files the intake accepts (`AssistantDocumentIntake.AllowedExtensions`). */
export const ASSISTANT_ACCEPT = ".pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg";

export interface AssistantPanelProps {
  threadId: string | undefined;
  className?: string;
  header?: ReactNode;
  /** Tighter chrome for side panels (case drawer, matter workbench, console test bench). */
  compact?: boolean;
  /** The console's test bench: shows each answer's model, response time and raw tool trace under a "Details" disclosure. */
  debug?: boolean;
  /** Sent once as the first message when the thread is empty (deep link `?q=`). */
  initialPrompt?: string | null;
  /** How the initial prompt is sent: a plain question or a research hand-off (deep link `?mode=research`). */
  initialMode?: "ask" | "research";
  /** A host action's message (a workbench "ask the agent" button), sent once per `id` whatever the thread already holds. */
  request?: PanelRequest | null;
  /** Processes the composer offers; without `onStartProcess` a choice is sent to the orchestrator as the turn's directive. */
  playbooks?: PlaybookOption[];
  /** Opens a process the host's own way (the open-a-file dialog); without it, the process is started through the conversation. */
  onStartProcess?: (playbookKey: string) => void;
  /** Host-provided navigation: artifacts panel, case pages, agent-run drawer, knowledge documents. */
  onOpenArtifact?: (artifactId: string) => void;
  onOpenCase?: (caseId: string) => void;
  onOpenRun?: (runId: string) => void;
  /** A case document (`sourceType` "Document") cited by the assistant — lives in the back office's case file. */
  onOpenDocument?: (documentId: string, chunkId?: string | null) => void;
  /** A knowledge-base document (any other `sourceType`) cited by the assistant; `documentId` is the corpus document id. */
  onOpenKnowledge?: (documentId: string, chunkId?: string | null) => void;
  /**
   * A statistics-warehouse read or model run (`sourceType` "DataBank"); `documentKey` names it (`series:…` or a bare series key — the value's
   * page in the Statistics tab —, `dataset:…`, `concept:…:geo`, `compare:…`, `source:…`, `target:…`).
   */
  onOpenData?: (documentKey: string) => void;
  /** Overrides how a confirmation card is decided; by default the panel calls the thread's confirmation endpoint. */
  onConfirm?: (confirmation: ToolConfirmation, accept: boolean, edits?: Record<string, unknown>) => Promise<void> | void;
  /** Opens a surface-relative link produced by a confirmed action (e.g. `/matters/{id}`); without it the link is not offered. */
  onOpenLink?: (link: string) => void;
  /** Called when the composer would send with no thread yet (lets a host create the thread lazily). */
  onSendWithoutThread?: (text: string) => void;
  /** Draws the UI blocks (charts, tables, maps…) an answer rendered; without it they are not shown. `key` is the message's id or "live".
   *  For an answer that places its widgets in its text (`extras.layout` "inline-1") it receives the answer's tail only (next steps,
   *  follow-ups, links); a placed widget is drawn by `renderInlineBlock`, or by this with that one block when the host gives none. */
  renderBlocks?: (blocks: AnswerBlock[], key: string) => ReactNode;
  /** Draws the widget a marker places in an answer's text (`[[w:<id>]]` alone on a line). The markers are checked first as the server
   *  checks them (a known block, placed once, at most two besides drafts), so a marker drawn while streaming stays once saved; `block` is
   *  null only should no block answer the id (draw nothing). `key` is the message's id or "live". A host that passes this is also the one
   *  whose live turns stream as prose with inline widgets (when the server's start frame says so); every other host keeps the earlier layout. */
  renderInlineBlock?: (block: AnswerBlock | null, ref: InlineWidgetRef, key: string) => ReactNode;
  /** A quiet line under each saved answer, above its footer (the ECONIC sidebar's "Sources used"); null draws nothing. */
  answerMeta?: (message: ThreadMessage) => ReactNode;
  /** The custom page open next to the conversation, read when each message is sent: the turn acts in it and knows what it shows. */
  page?: () => OpenPage | null | undefined | Promise<OpenPage | null | undefined>;
  /** The agent called one of the open page's client tools (set a lever, focus a country): hand it to the page. */
  onClientCall?: (call: ClientToolCall) => void;
  /** Starter questions from the host (the open page's, for its current tab); when there are any they replace the assistant's own. */
  starters?: StarterPrompt[] | null;
  /** The empty conversation's heading from the host ("How can I help with …?"); without it the assistant's greeting. */
  greeting?: string | null;
  /**
   * Puts a question in the composer, unsent, once per `id` (a page button that asks the agent to look at something). `directive` (a card's
   * `next <kind>`) goes with those words when the person sends them, until the composer is emptied.
   */
  prefill?: { id: string; text: string; directive?: string | null } | null;
  /** False hides the Ask / Research / Process choice on the empty conversation. */
  startModes?: boolean;
  /** How many starter cards the empty conversation shows; by default 2 when compact, else 4. */
  maxStarters?: number;
  // ---- ECONIC (builder §15.4, §17; DESIGN-SYSTEM §10.15)
  /** The screen the person is on, read when each message is sent (`SendMessageRequest.surface`). */
  surface?: () => SurfaceState | null | undefined;
  /** The sources of knowledge the person chose (the sources row), read when each message is sent (`SendMessageRequest.sources`); a
   * {@link TurnSourceChoice} adds the Knowledge Bank datasets chosen (`SendMessageRequest.datasets`). */
  sources?: () => KnowledgeSourceKey[] | TurnSourceChoice | null | undefined;
  /** A builder change: once its turn settles, and again after an Undo from its op card (status "undone"). */
  onUi?: (event: UiEvent) => void;
  /** What the model was given about the screen (the turn's `status: surface` frame). */
  onSurfaceSeen?: (seen: SurfaceSeen) => void;
  /** A turn failed with this plain reason ("The language model provider's usage limit is reached…"). */
  onTurnError?: (message: string, code?: string | null) => void;
  /** A turn ended; `ok` when the model answered without an error. */
  onTurnSettled?: (ok: boolean) => void;
  /** "inspector" draws the conversation as the ECONIC Agent's inspector; "custom" hands it to `renderChrome`; "panel" (default) is the NCP chat panel. */
  chrome?: "panel" | "inspector" | "custom";
  /** Draws the conversation when `chrome` is "custom" (the ECONIC Agent full screen). */
  renderChrome?: (props: PanelChromeProps) => ReactNode;
  inspector?: InspectorSlots;
  /** Steers every send from the host's controls (Build mode, department scope); joined with the composer's own directive. */
  directive?: string | null;
  /** The host's Research control: sends go to the evidence researcher. */
  research?: boolean;
  /** Under the person's words while the turn runs ("For my department"). */
  scopeLine?: string | null;
  /** Opens a cited source the host's own way (a Knowledge Bank version and page); return true when handled. */
  onOpenSource?: (citation: MessageCitation) => boolean;
  /** The name of the screen a message was sent from, for the "now on …" dividers. */
  contextLabel?: (context: MessageContext) => string | null | undefined;
  /** The name of a surface for an op card's "Where" line. */
  surfaceLabel?: (surface: string) => string | null | undefined;
  /** Opens a route of the host (an op card's preview of a department draft, the screen that changed). */
  onNavigate?: (route: string) => void;
  /** A view-only account: answers offer no feedback and no saving as a draft (both write; the server refuses them too). */
  readOnly?: boolean;
  /** What the agent is doing (listening, thinking, working, speaking, done, asking, error), each time it changes: the host's faces. */
  onPresence?: (presence: Presence) => void;
}

/**
 * The AI Workspace conversation: streams a turn over SSE, folds the work behind each answer into one line, renders proposals, drafts and
 * actions to confirm inline, follows delegated specialists in real time, and offers processes, specialists and dictation in the composer.
 * Built on `@ncp/ui`'s chat kit, so it reads the same in the workspace app, the back office and the console's test bench.
 */
export function AssistantPanel({ threadId, className, header, compact = false, debug = false, initialPrompt, initialMode = "ask", request, playbooks = [], onStartProcess, onOpenArtifact, onOpenCase, onOpenRun, onOpenDocument, onOpenKnowledge, onOpenData, onConfirm, onOpenLink, onSendWithoutThread, renderBlocks, renderInlineBlock, answerMeta, page, onClientCall, starters, greeting: greetingOverride, prefill, startModes = true, maxStarters,
  surface, sources, onUi, onSurfaceSeen, onTurnError, onTurnSettled, chrome = "panel", renderChrome, inspector, directive: hostDirective, research = false, scopeLine, onOpenSource, contextLabel, surfaceLabel, onNavigate, readOnly = false, onPresence }: AssistantPanelProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const detail = useThread(threadId);
  const { live, streaming, send, abort, dismissError } = useAssistantStream(threadId, { page, onClientCall, surface, sources, onUi, onSurfaceSeen, onSettled: onTurnSettled, onModelUnavailable: onTurnError ? (reason) => onTurnError(reason, "model_unavailable") : undefined });
  // A host-drawn conversation reads like the inspector (op cards, day dividers); only the layout is the host's.
  const inspectorChrome = chrome === "inspector" || chrome === "custom";
  const feedback = useMessageFeedback(threadId ?? "");
  const attach = useAttach(threadId);
  const { mutateAsync: decideAsync } = useDecideConfirmation(threadId);
  const decide = useCallback(async (c: ToolConfirmation, accept: boolean, edits?: Record<string, unknown>) => {
    if (onConfirm) { await onConfirm(c, accept, edits); return; }
    try {
      const r = await decideAsync({ confirmationId: c.id, accept, edits });
      if (r.confirmation.status === "Failed") toast.error(t("ai.chat.confirmFailed"), { description: r.confirmation.error ?? undefined });
    } catch (e) {
      notifySaveFailed(e, t("ai.chat.confirmFailed"));
    }
  }, [onConfirm, decideAsync, t]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const data = detail.data;
  const assistant = data?.assistant ?? null;
  const assistantName = useMemo(() => localName(assistant, locale) ?? t("ai.assistant"), [assistant, locale, t]);
  const specialistName = useCallback((key: string, fallback?: string | null) => {
    const d = assistant?.delegates.find((x) => x.key === key);
    return d ? (locale === "ar" && d.nameAr ? d.nameAr : d.name) : fallback ?? key;
  }, [assistant, locale]);

  // ---- proposals referenced by messages (fetched through the thread-scoped inbox, which carries citations and case numbers)
  const proposalIds = useMemo(() => new Set((data?.messages ?? []).flatMap((m) => m.proposalIds)), [data]);
  const inbox = useInbox({ threadId, pageSize: 100 }, { enabled: !!threadId && proposalIds.size > 0 });
  const proposalsById = useMemo(() => new Map((inbox.data?.items ?? []).map((i) => [i.proposal.id, i.proposal] as const)), [inbox.data]);
  const invalidate = useMemo(() => [assistantKeys.thread(threadId ?? ""), ["assistant", "inbox"], ["assistant", "home"]] as const, [threadId]);

  // ---- live steps of delegated runs (SignalR `thread.step`), grouped by run
  const [stepsByRun, setStepsByRun] = useState<Record<string, AgentStepLike[]>>({});
  useEffect(() => setStepsByRun({}), [threadId]);
  useThreadRealtime(threadId, useCallback((p: ThreadStepPayload) => {
    if (!p.step) return;
    const step = p.step as AgentStepLike;
    setStepsByRun((s) => {
      const list = s[p.runId] ?? [];
      if (list.some((x) => x.id === step.id)) return s;
      return { ...s, [p.runId]: [...list, step].slice(-40) };
    });
  }, []));

  // ---- attachments waiting to go with the next message
  const [pendingFiles, setPendingFiles] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState<string[]>([]);
  useEffect(() => { setPendingFiles([]); setUploading([]); }, [threadId]);
  const onAttach = async (files: File[]) => {
    if (!threadId) return;
    setUploading((u) => [...u, ...files.map((f) => f.name)]);
    try {
      const r = await attach.mutateAsync(files);
      setPendingFiles((p) => [...p, ...r.attachments]);
      for (const e of r.errors) toast.error(t("ai.chat.uploadFailed", { name: e }));
    } catch (e) {
      notifySaveFailed(e, t("ai.chat.uploadFailed", { name: files.map((f) => f.name).join(", ") }));
    } finally {
      setUploading((u) => u.filter((n) => !files.some((f) => f.name === n)));
    }
  };

  // ---- composer: controlled draft with @specialist and /process completion, and the mode the next message goes out in
  const [draft, setDraft] = useState("");
  /** The directive of the words a host prefilled (a card's `next <kind>`), until they are sent or the composer is emptied. */
  const prefillDirective = useRef<string | null>(null);
  useEffect(() => { if (!draft.trim()) prefillDirective.current = null; }, [draft]);
  useEffect(() => { prefillDirective.current = null; }, [threadId]);
  const [mode, setMode] = useState<ComposerMode>({ kind: "ask" });
  const [startMode, setStartMode] = useState<StartMode>("ask");
  useEffect(() => { setMode({ kind: "ask" }); setStartMode("ask"); }, [threadId]);
  const completion = useCompletion(draft, assistant, playbooks, locale);
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => setActiveIndex(0), [completion?.query, completion?.kind]);
  const pick = (item: CompletionItem) => {
    if (!completion) return;
    setDraft(draft.slice(0, completion.start) + (completion.kind === "mention" ? "@" : "/") + item.key + " " + draft.slice(completion.end));
    inputRef.current?.focus();
  };
  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!completion || completion.items.length === 0) return false;
    if (e.key === "ArrowDown") { setActiveIndex((i) => (i + 1) % completion.items.length); return true; }
    if (e.key === "ArrowUp") { setActiveIndex((i) => (i - 1 + completion.items.length) % completion.items.length); return true; }
    if (e.key === "Enter" || e.key === "Tab") { pick(completion.items[activeIndex] ?? completion.items[0]); return true; }
    if (e.key === "Escape") { setDraft(draft.slice(0, completion.start) + draft.slice(completion.end)); return true; }
    return false;
  };

  const doSend = (text: string, attachments = pendingFiles, as: ComposerMode = mode, extra: { directive?: string | null; focus?: SurfaceFocus | null; scope?: string | null; replaces?: string | null } = {}) => {
    if (!threadId) { onSendWithoutThread?.(text); return; }
    const mode: ComposerMode = research && as.kind === "ask" ? { kind: "research" } : as;
    // A prefill's directive goes with its words, sent from the composer (not with a follow-up or a next step sent meanwhile).
    const carried = prefillDirective.current && text.trim() === draft.trim() ? prefillDirective.current : null;
    if (text.trim() === draft.trim()) prefillDirective.current = null;
    // "answers" is a reserved directive the server stores as `extras.answers`; it is sent alone.
    const directive = extra.directive === "answers" ? "answers" : joinDirectives(directiveFor(text, mode, assistant, playbooks, locale), hostDirective, carried, extra.directive);
    void send(text, { attachments, locale, directive, focus: extra.focus ?? null, scope: extra.scope ?? scopeLine ?? null, replaces: extra.replaces ?? null });
    setPendingFiles([]);
    setDraft("");
    setMode({ kind: "ask" });
  };
  const sendFollowUp = (text: string, options?: { scope?: "department" }) => {
    doSend(text, [], { kind: "ask" }, options?.scope === "department"
      ? { directive: DEPARTMENT_DIRECTIVE, scope: t("assistant:econic.sidebar.scope.forDepartment", { defaultValue: "For my department" }) }
      : {});
  };
  const answer = (text: string) => doSend(text, [], { kind: "ask" }, { directive: "answers" });

  const startProcess = (key: string) => {
    if (onStartProcess) { onStartProcess(key); return; }
    setDraft(`/${key} `);
    inputRef.current?.focus();
  };
  const changeStartMode = (m: StartMode) => {
    setStartMode(m);
    if (m === "research") setMode({ kind: "research" });
    else if (m === "ask") setMode({ kind: "ask" });
    inputRef.current?.focus();
  };

  // Deep link: send the initial prompt once the (empty) thread is loaded.
  const sentInitial = useRef<string | null>(null);
  useEffect(() => {
    if (!initialPrompt || !threadId || !data || streaming) return;
    if (sentInitial.current === threadId) return;
    if (data.messages.length > 0) { sentInitial.current = threadId; return; }
    sentInitial.current = threadId;
    doSend(initialPrompt, [], initialMode === "research" ? { kind: "research" } : { kind: "ask" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, threadId, data, streaming]);

  const sentRequest = useRef<string | null>(null);
  useEffect(() => {
    if (!request || !threadId || !data || streaming || sentRequest.current === request.id) return;
    sentRequest.current = request.id;
    doSend(request.text, [], { kind: "ask" }, {
      focus: request.focus ?? null,
      directive: request.scope === "department" ? joinDirectives(DEPARTMENT_DIRECTIVE, request.directive) : request.directive ?? null,
      scope: request.scope === "department" ? t("assistant:econic.sidebar.scope.forDepartment", { defaultValue: "For my department" }) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, threadId, data, streaming]);

  // A failed turn's reason reaches the host once ("AI unavailable · data still live").
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (!live?.error) return;
    const key = `${live.startedAt}:${live.error}`;
    if (reported.current === key) return;
    reported.current = key;
    onTurnError?.(live.error, live.errorCode);
  }, [live?.error, live?.errorCode, live?.startedAt, onTurnError]);

  // A host prefill lands in the composer once per id, unsent, with the caret at its end so the person can add to it; its directive waits
  // for those words to be sent and is dropped once the composer is emptied.
  const prefilled = useRef<string | null>(null);
  useEffect(() => {
    if (!prefill || prefilled.current === prefill.id) return;
    prefilled.current = prefill.id;
    prefillDirective.current = prefill.directive?.trim() || null;
    setDraft(prefill.text);
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }, [prefill]);

  const speech = useSpeechInput(locale === "ar" ? "ar-AE" : "en-GB", (text) => setDraft((d) => (d ? `${d} ${text}` : text)));

  // ---- what the agent is doing, for the host's faces (ECONIC, 23 Oct 2026)
  const presence = useAgentPresence({ busy: streaming || uploading.length > 0, streaming, live, listening: speech.listening });
  const onPresenceRef = useRef(onPresence);
  onPresenceRef.current = onPresence;
  useEffect(() => {
    onPresenceRef.current?.(presence);
  }, [presence.state, presence.agentKey]);

  // ---- citations → source drawer
  const [citation, setCitation] = useState<CitationLike | null>(null);
  const citationMeta = useRef(new Map<string, MessageCitation>());
  const withheldText = t("assistant:econic.sidebar.withheld", { defaultValue: "This passage is restricted. The assistant saw the title only." });
  const pageText = (n: number) => t("assistant:econic.sidebar.page", { defaultValue: "p. {{n}}", n });
  const toCitation = useCallback((c: MessageCitation, scope: string): CitationLike => {
    const id = `${scope}:${c.index}`;
    citationMeta.current.set(id, c);
    // A restricted Knowledge Bank passage arrives without its text (KB §7.2): say so instead of an empty card.
    const excerpt = c.withheld ? withheldText : c.excerpt;
    const locator = [c.locator, c.page && !c.withheld ? pageText(c.page) : null].filter(Boolean).join(" · ") || null;
    return {
      id, index: c.index, sourceType: normaliseSourceType(c.sourceType), sourceId: c.sourceId, title: c.title, locator, excerpt,
      // A statistics value's or a web page's provenance: the page it opens and its dates (never a withheld passage's).
      url: c.withheld ? null : c.url ?? (c.sourceType === "Web" ? c.documentKey : null), publisher: c.publisher, period: c.period, published: c.published, checked: c.checked,
      documentKey: c.sourceType === "DataBank" ? c.documentKey : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withheldText]);
  const citationDoc = citation ? citationMeta.current.get(citation.id) : undefined;
  // Case documents carry their id in `documentId`; warehouse reads have no document, only their `documentKey`; corpus citations identify the
  // knowledge document through `sourceId`. A host that opens Knowledge Bank versions itself (`onOpenSource`) goes first.
  // A web page opens its own address in a new tab (its drawer also links it).
  const webTarget = citationDoc?.sourceType === "Web" ? (citationDoc.url ?? citationDoc.documentKey ?? null) : null;
  const citationTarget = !citationDoc ? null
    : onOpenSource && (citationDoc.versionId || citationDoc.portalKey) ? () => { onOpenSource(citationDoc); }
    : citationDoc.sourceType === "Document" ? (citationDoc.documentId && onOpenDocument ? () => onOpenDocument(citationDoc.documentId!, citationDoc.chunkId) : null)
    : citationDoc.sourceType === "DataBank" ? (citationDoc.documentKey && onOpenData ? () => onOpenData(citationDoc.documentKey!) : null)
    : citationDoc.sourceType === "Web" ? (webTarget && /^https?:\/\//i.test(webTarget) ? () => { window.open(webTarget, "_blank", "noopener,noreferrer"); } : null)
    : (citationDoc.sourceId && onOpenKnowledge ? () => onOpenKnowledge(citationDoc.sourceId, citationDoc.chunkId) : null);

  // ---- messages
  // One key per answer from its first token to its saved form (and for the question before it): the swap from live to saved remounts nothing.
  const answerKeys = useRef(new AnswerKeys());
  useEffect(() => answerKeys.current.clear(), [threadId]);
  /** Draws a placed widget: the host's inline renderer, else its block renderer with that one block. */
  const widgetRenderer = useCallback((byId: Map<string, AnswerBlock>, key: string) => (ref: InlineWidgetRef): ReactNode => {
    const block = byId.get(ref.id) ?? null;
    if (renderInlineBlock) return renderInlineBlock(block, ref, key);
    return block && renderBlocks ? renderBlocks([block], key) : null;
  }, [renderInlineBlock, renderBlocks]);
  const entries = useMemo<InspectorEntry[]>(() => {
    const out: InspectorEntry[] = [];
    if (live) answerKeys.current.note(live.messageId ?? live.final?.messageId, live.key);
    if (!data) return out;
    // A finished turn whose saved reply the thread already holds is drawn from the thread only (never twice, never neither).
    const lv = live && !(live.final && !streaming && data.messages.some((m) => m.id === live.final!.messageId)) ? live : null;
    // While the live turn is still drawn, its saved copy (should the thread already hold it) keeps its own id as its key: never two alike.
    const keyOf = entryKeys(answerKeys.current, lv ? lv.messageId ?? lv.final?.messageId ?? null : null);
    const forDepartment = t("assistant:econic.sidebar.scope.forDepartment", { defaultValue: "For my department" });
    const nowOn = (label: string) => t("assistant:econic.sidebar.nowOn", { defaultValue: "Now on {{where}}", where: label });
    const labelOf = (c: MessageContext) => contextLabel?.(c) ?? (locale === "ar" && c.titleAr ? c.titleAr : c.title) ?? null;
    let lastContext: string | null = null;
    // The latest change of each surface in the thread: only its card offers Undo (Undo undoes the last change).
    const latestUi = new Map<string, string>();
    for (const m of data.messages) (m.extras?.ui ?? []).forEach((u, i) => latestUi.set(u.surface, `${m.id}:${i}`));
    const lastAssistant = [...data.messages].reverse().find((m) => m.role === "Assistant" || m.role === "User");
    const opCards = (events: UiEvent[], key: string, latest: (u: UiEvent, i: number) => boolean) => events.length > 0 ? (
      <div className="ea-ops">
        {events.map((u, i) => <OpCard key={`${key}:${i}`} event={u} latest={latest(u, i)} where={surfaceLabel?.(u.surface) ?? null} onNavigate={onNavigate} onSend={threadId ? sendFollowUp : undefined} onChanged={onUi} />)}
      </div>
    ) : null;
    // In the inspector a staged action reads as an op card (status text, one action group, the default last); elsewhere the NCP card.
    const confirmCard = (c: ToolConfirmation) => inspectorChrome
      ? <OpConfirmCard confirmation={c} locale={locale} debug={debug} onDecide={threadId ? decide : undefined} onOpenLink={onOpenLink} />
      : <ConfirmCard confirmation={c} locale={locale} debug={debug} onDecide={threadId ? decide : undefined} onOpenLink={onOpenLink} />;
    // Day dividers: before the first message of each day, "Today · 08:22" (APPLE-SPEC §6.3), in the inspector only.
    let lastDay: string | null = null;
    const dayDivider = (m: { id: string; createdAt: string }) => {
      if (!inspectorChrome) return;
      const when = new Date(m.createdAt);
      if (Number.isNaN(when.getTime())) return;
      const day = when.toDateString();
      if (day === lastDay) return;
      lastDay = day;
      out.push({ kind: "divider", id: `day-${m.id}`, label: dayLabel(when, locale, t), tone: "day" });
    };
    const restrictedOf = (cs: Array<Pick<MessageCitation, "index" | "withheld" | "sensitivity">>) => cs.filter((c) => c.withheld || /restricted|confidential/i.test(c.sensitivity ?? "")).map((c) => c.index);
    // `reply`: what the person sent after the questions (null: nothing yet), so an answered card marks what they chose.
    const askCard = (ask: AskUserEvent | null | undefined, answerable: boolean, reply: string | null) => ask && ask.questions.length > 0
      ? <AskUserCard ask={ask} answerable={answerable && !!threadId} onAnswer={answer} reply={reply} />
      : null;
    // A confirmation shows as a card under the reply that staged it; the outcome line posted on decision links to what it did.
    const confirmations = new Map((data.confirmations ?? []).map((c) => [c.id, c] as const));
    for (const c of data.pendingConfirmations) if (!confirmations.has(c.id)) confirmations.set(c.id, c);
    const placed = new Set<string>();
    // The latest answer can be asked again: it carries the question that preceded it.
    // A turn asked again ("Regenerate", "Edit and resend") leaves the transcript: at once while its replacement streams, for good once saved.
    let visible = data.messages.filter((m) => m.role !== "Tool" && !m.extras?.superseded);
    const replacedAt = lv?.replaces ? visible.findIndex((m) => m.id === lv.replaces) : -1;
    if (replacedAt >= 0) visible = visible.slice(0, replacedAt);
    const lastAnswer = !lv && !streaming ? [...visible].reverse().find((m) => m.role === "Assistant") : undefined;
    const retryOf = lastAnswer ? [...visible.slice(0, visible.indexOf(lastAnswer))].reverse().find((m) => m.role === "User") : undefined;
    const retryText = retryOf?.content;
    // The person's next message after a reply, else the turn they are sending now: their answers to its questions, or words of their own.
    const replyAfter = (m: ThreadMessage) => visible.slice(visible.indexOf(m) + 1).find((x) => x.role === "User")?.content
      ?? (lv && (lv.messageId ?? lv.final?.messageId) !== m.id ? lv.userText : null);
    for (const m of visible) {
      const confirmation = m.confirmationId ? confirmations.get(m.confirmationId) : undefined;
      if (m.role === "Assistant" && confirmation && confirmation.messageId !== m.id) {
        out.push({ id: m.id, role: "assistant", content: m.content, author: t("ai.chat.actionResult"), after: (
          <>
            {opCards(m.extras?.ui ?? [], m.id, (u, i) => latestUi.get(u.surface) === `${m.id}:${i}`)}
            <ConfirmationResult confirmation={confirmation} onOpenLink={onOpenLink} />
          </>
        ) });
        continue;
      }
      if (confirmation) placed.add(confirmation.id);
      if (m.role === "User") {
        dayDivider(m);
        const ctx = m.extras?.context;
        const key = ctx ? ctx.surface || ctx.route || null : null;
        if (ctx && key && lastContext !== null && key !== lastContext) {
          const label = labelOf(ctx);
          if (label) out.push({ kind: "divider", id: `ctx-${m.id}`, label: nowOn(label) });
        }
        if (key) lastContext = key;
        // A change for the department reads "For my department" under the person's words.
        const next = visible[visible.indexOf(m) + 1];
        const departmental = next?.role === "Assistant" && (next.extras?.ui ?? []).some((u) => u.scope === "department");
        const answered = !!m.extras?.answers;
        out.push({ id: m.id, key: keyOf.question(m.id, next?.role === "Assistant" ? next.id : null), role: "user", content: m.content, scope: departmental ? forDepartment : answered ? t("assistant:econic.sidebar.ask.yourAnswers", { defaultValue: "Your answers" }) : null, attachments: m.attachments.map((a) => ({ name: a.fileName, status: "uploaded" as const })) });
        continue;
      }
      if (m.role === "System") {
        if (m.extras?.divider || m.extras?.handoff) out.push({ kind: "divider", id: m.id, label: m.content, tone: "handoff" });
        else out.push({ id: m.id, role: "system", content: "", bare: true, before: <SystemNote>{m.content}</SystemNote> });
        continue;
      }
      // An answer that places its widgets in its text draws them there; its tail follows it. An earlier answer keeps its layout.
      const layout = answerLayout(m.content, m.blocks, isInlineMessage(m));
      out.push({
        id: m.id,
        key: keyOf.answer(m.id),
        role: "assistant",
        // an inline answer's text with its markers checked (on a saved answer, the server's own): the widgets the text places, once
        content: layout.content,
        renderWidget: layout.inline ? widgetRenderer(layout.byId, m.id) : undefined,
        author: m.agentKey ? t("ai.chat.specialistReply", { agent: specialistName(m.agentKey) }) : undefined,
        citations: m.citations.map((c) => toCitation(c, m.id)),
        restricted: restrictedOf(m.citations),
        before: m.toolCalls.length > 0 ? <TurnSteps calls={traceOf(m)} specialistName={specialistName} /> : undefined,
        after: (
          <>
            {renderBlocks && layout.after.length ? renderBlocks(layout.after, m.id) : null}
            {answerMeta?.(m) ?? null}
            {opCards(m.extras?.ui ?? [], m.id, (u, i) => latestUi.get(u.surface) === `${m.id}:${i}`)}
            {askCard(m.extras?.ask && sameText(m.extras.ask.intro, m.content) ? { ...m.extras.ask, intro: null } : m.extras?.ask, !lv && !streaming && m === lastAssistant, m.extras?.ask ? replyAfter(m) : null)}
            {confirmation ? confirmCard(confirmation) : null}
            {threadId ? (
              <AnswerFooter
                message={m}
                threadId={threadId}
                detail={data}
                proposals={m.proposalIds.map((id) => proposalsById.get(id)).filter((p): p is AiProposal => !!p)}
                invalidate={invalidate}
                compact={compact}
                debug={debug}
                onOpenArtifact={onOpenArtifact}
                onOpenCase={onOpenCase}
                onOpenRun={onOpenRun}
                onRetry={m === lastAnswer && retryText ? () => doSend(retryText, [], { kind: "ask" }, { replaces: retryOf?.id ?? null }) : undefined}
                readOnly={readOnly}
                onFeedback={(value) => feedback.mutate({ messageId: m.id, feedback: m.feedback === value ? null : value }, { onSuccess: (r) => { if (m.feedback !== value) notifySaved(r, t("ai.chat.feedbackThanks")); }, onError: (e) => notifySaveFailed(e) })}
              />
            ) : null}
          </>
        ),
      });
    }
    for (const c of data.pendingConfirmations.filter((x) => !placed.has(x.id))) {
      out.push({ id: `confirm-${c.id}`, role: "system", content: "", bare: true, before: confirmCard(c) });
    }
    for (const r of data.runs.filter((x) => x.status === "Running" || x.status === "Queued")) {
      out.push({ id: `run-${r.id}`, role: "assistant", content: "", author: t("ai.chat.specialistReply", { agent: specialistName(r.agentKey, r.agentName) }), before: <RunProgress run={r} steps={stepsByRun[r.id] ?? []} onOpenRun={onOpenRun} /> });
    }
    if (lv) {
      dayDivider({ id: "live", createdAt: new Date(lv.startedAt ?? Date.now()).toISOString() });
      const key = lv.surface ? lv.surface.surface || lv.surface.route : null;
      if (lv.surface && key && lastContext !== null && key !== lastContext) {
        const label = labelOf({ surface: lv.surface.surface, route: lv.surface.route, title: lv.surface.title, titleAr: lv.surface.titleAr, kind: lv.surface.kind });
        if (label) out.push({ kind: "divider", id: "ctx-live", label: nowOn(label) });
      }
      // Prose with inline widgets only when the server says so and this host draws them; every other live turn keeps the earlier layout.
      const liveLayout = answerLayout(lv.content, lv.blocks, isInlineLive(lv, !!renderInlineBlock));
      out.push({ id: "live-user", key: questionKey(lv.key), role: "user", content: lv.userText, scope: lv.scope ?? null, attachments: lv.attachments.map((a) => ({ name: a.fileName, status: "uploaded" as const })) });
      out.push({
        id: "live-assistant",
        key: lv.key,
        role: "assistant",
        // as it will be saved: a repeated, third or unknown marker draws nothing while the answer streams
        content: liveLayout.content,
        streaming: streaming && !lv.error,
        renderWidget: liveLayout.inline ? widgetRenderer(liveLayout.byId, "live") : undefined,
        citations: lv.citations.map((c) => toCitation({ index: c.index, sourceType: c.sourceType, sourceId: c.sourceId, title: c.title, locator: c.locator, excerpt: c.excerpt, documentId: c.documentId, chunkId: c.chunkId, documentKey: c.documentKey,
          page: c.page, versionId: c.versionId, versionNumber: c.versionNumber, withheld: c.withheld, portalKey: c.portalKey, sensitivity: c.sensitivity,
          url: c.url, publisher: c.publisher, period: c.period, published: c.published, checked: c.checked }, "live")),
        restricted: restrictedOf(lv.citations),
        // Prose with inline widgets: one line where the text will start, until it does. The earlier layout: the steps as they run.
        before: liveLayout.inline
          ? <LiveLine live={lv} streaming={streaming} progress={liveLayout.progress} specialistName={specialistName} />
          : <LiveSteps live={lv} streaming={streaming} specialistName={specialistName} />,
        // The card becomes actionable once the reply is saved, so the outcome line always lands after it.
        after: (
          <>
            {renderBlocks && liveLayout.after.length ? renderBlocks(liveLayout.after, "live") : null}
            {opCards(lv.ui, "live", () => true)}
            {askCard(lv.ask, !streaming, null)}
            <LiveExtras live={lv} stepsByRun={stepsByRun} specialistName={specialistName} onOpenArtifact={onOpenArtifact} onOpenRun={onOpenRun} onDecide={streaming ? undefined : decide} saved={confirmations} locale={locale} debug={debug} onRetry={() => { dismissError(); doSend(lv.userText, lv.attachments, { kind: "ask" }); }} onDismiss={dismissError} inspector={inspectorChrome} />
          </>
        ),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, live, streaming, stepsByRun, proposalsById, locale, compact, debug, specialistName, toCitation, invalidate, decide, onOpenLink, threadId, renderBlocks, renderInlineBlock, widgetRenderer, answerMeta, contextLabel, surfaceLabel, onNavigate, onUi, inspectorChrome, readOnly]);
  // The NCP panel draws a divider as a centred note.
  const messages = useMemo<ChatMessageLike[]>(() => entries.map((e) => (e.kind === "divider"
    ? { id: e.id, role: "system" as const, content: "", bare: true, before: <SystemNote>{e.label}</SystemNote> }
    : e)), [entries]);

  const prompts = useMemo<StarterPrompt[]>(() => {
    const own = (starters ?? []).filter((p) => p.text);
    return own.length > 0 ? own : (assistant?.suggestedPrompts ?? []).map((p) => starter(p, locale)).filter((p) => p.text);
  }, [starters, assistant, locale]);
  const greeting = greetingOverride || (assistant ? (locale === "ar" && assistant.greetingAr ? assistant.greetingAr : assistant.greeting) : null);
  const disclaimer = (assistant ? (locale === "ar" && assistant.disclaimerAr ? assistant.disclaimerAr : assistant.disclaimer) : null) || t("ai.chat.disclaimer");

  const composerTop = mode.kind !== "ask" || pendingFiles.length > 0 || uploading.length > 0 ? (
    <>
      <ModeChip mode={mode} assistant={assistant} locale={locale} onClear={() => { setMode({ kind: "ask" }); setStartMode("ask"); }} />
      {pendingFiles.length > 0 || uploading.length > 0 ? (
        <PromptInputAttachments>
          {pendingFiles.map((a) => <PromptInputAttachment key={a.documentId} name={a.fileName} status="uploaded" removeLabel={t("ai.chat.removeAttachment")} onRemove={() => setPendingFiles((p) => p.filter((x) => x.documentId !== a.documentId))} />)}
          {uploading.map((n) => <PromptInputAttachment key={n} name={n} status="uploading" />)}
        </PromptInputAttachments>
      ) : null}
    </>
  ) : undefined;

  const sourceDrawer = (
    <SourceDrawer
      citation={citation}
      onClose={() => setCitation(null)}
      extra={citationTarget ? <Button variant="outline" size="sm" className="mt-4" onClick={() => { citationTarget(); setCitation(null); }}><FileText /> {t("ai.chat.viewSource")}</Button> : undefined}
    />
  );

  if (chrome === "custom" && renderChrome) {
    return (
      <>
        {renderChrome({
          entries,
          busy: streaming || uploading.length > 0,
          streaming,
          loading: detail.isLoading && !!threadId,
          live,
          draft,
          setDraft,
          send: (text, options) => {
            const body = (text ?? draft).trim();
            if (!body || streaming) return;
            doSend(body, pendingFiles, options?.research ? { kind: "research" } : mode, { ...(options?.directive ? { directive: options.directive } : {}), replaces: options?.replaces ?? null });
          },
          stop: abort,
          inputRef,
          onComposerKeyDown: (e) => !!onComposerKeyDown(e),
          completion: completion ? <CompletionMenu completion={completion} activeIndex={activeIndex} onPick={pick} onHover={setActiveIndex} /> : null,
          attach: assistant?.allowAttachments === false || !threadId ? undefined : (files) => void onAttach(files),
          accept: ASSISTANT_ACCEPT,
          pending: { files: pendingFiles, uploading, remove: (id) => setPendingFiles((p) => p.filter((x) => x.documentId !== id)) },
          openCitation: setCitation,
          assistantName,
          assistant,
          speech: assistant?.allowVoice !== false && speech.supported ? speech : null,
          presence,
        })}
        {sourceDrawer}
      </>
    );
  }

  if (inspectorChrome) {
    // The page's suggestions stay offered after every turn: a group after a short thread, quiet chips after a long one (InspectorChat).
    const chipTexts = (inspector?.chips?.length ? inspector.chips : prompts.map((p) => p.text)).filter(Boolean);
    return (
      <>
        <InspectorChat
          entries={entries}
          onSend={(text) => doSend(text)}
          onStop={abort}
          busy={streaming || uploading.length > 0}
          draft={draft}
          onDraftChange={setDraft}
          onComposerKeyDown={onComposerKeyDown}
          inputRef={inputRef}
          onAttach={assistant?.allowAttachments === false || !threadId ? undefined : (files) => void onAttach(files)}
          accept={ASSISTANT_ACCEPT}
          placeholder={inspector?.placeholder ?? (research || mode.kind === "research" ? t("ai.chat.placeholderResearch") : t("ai.chat.placeholder", { assistant: assistantName }))}
          onOpenCitation={setCitation}
          assistantName={assistantName}
          thinking={t("ai.chat.stage.thinking")}
          emptyState={detail.isLoading
            ? <div className="ea-loading" role="status" aria-label={t("app.loading")}><span /><span /><span /></div>
            : inspector?.emptyState ?? (greeting ? <p className="ea-greeting" dir="auto">{greeting}</p> : null)}
          composerTop={composerTop}
          routeLine={inspector?.routeLine}
          // The inspector's bar holds the host's controls only (DESIGN-SYSTEM §00.9); specialists stay reachable by typing "@", processes by "/".
          controls={inspector?.controls}
          footer={completion ? <CompletionMenu completion={completion} activeIndex={activeIndex} onPick={pick} onHover={setActiveIndex} /> : undefined}
          chips={chipTexts.map((text) => ({ text, onPick: () => doSend(text, [], { kind: "ask" }) }))}
          note={inspector?.note}
          labels={{
            send: t("actions.send"),
            stop: t("ai.chat.stop"),
            attach: t("ai.chat.attach"),
            earlier: (n) => t("assistant:econic.sidebar.earlier", { count: n, defaultValue: n === 1 ? "Show 1 earlier message" : "Show {{count}} earlier messages" }),
            suggested: t("assistant:econic.sidebar.suggested", { defaultValue: "Suggested for this page" }),
            latest: t("assistant:econic.sidebar.latest", { defaultValue: "Latest" }),
          }}
        />
        {sourceDrawer}
      </>
    );
  }

  return (
    <>
      <ChatPanel
        className={className}
        bodyClassName={compact ? "px-3 py-4 space-y-5" : undefined}
        messages={messages}
        onSend={(text) => doSend(text)}
        onStop={abort}
        draft={draft}
        onDraftChange={setDraft}
        onComposerKeyDown={onComposerKeyDown}
        inputRef={inputRef}
        onOpenCitation={setCitation}
        onAttach={assistant?.allowAttachments === false || !threadId ? undefined : (files) => void onAttach(files)}
        accept={ASSISTANT_ACCEPT}
        placeholder={mode.kind === "research" ? t("ai.chat.placeholderResearch") : t("ai.chat.placeholder", { assistant: assistantName })}
        busy={streaming || uploading.length > 0}
        assistantName={assistantName}
        labels={{ send: t("actions.send"), escalate: t("actions.escalate"), thinking: t("ai.chat.stage.thinking"), attach: t("ai.chat.attach"), stop: t("ai.chat.stop") }}
        header={header}
        footer={completion ? <CompletionMenu completion={completion} activeIndex={activeIndex} onPick={pick} onHover={setActiveIndex} /> : undefined}
        composerTop={composerTop}
        emptyState={detail.isLoading
          ? <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> {t("app.loading")}</div>
          : <ChatEmptyState greeting={greeting} mode={startMode} onModeChange={changeStartMode} prompts={prompts} playbooks={playbooks} compact={compact} modes={startModes} max={maxStarters} onStartProcess={playbooks.length > 0 ? startProcess : undefined} onPrompt={(text, m) => doSend(text, [], m === "research" ? { kind: "research" } : { kind: "ask" })} />}
        composerStart={
          <ComposerTools
            assistant={assistant}
            playbooks={playbooks}
            locale={locale}
            disabled={streaming}
            onStartProcess={playbooks.length > 0 ? startProcess : undefined}
            onAskSpecialist={(key) => { setMode({ kind: "specialist", key }); inputRef.current?.focus(); }}
            speech={assistant?.allowVoice !== false ? speech : null}
          />
        }
        disclaimer={compact ? undefined : disclaimer}
      />
      {sourceDrawer}
    </>
  );
}

/** The host directive for a change for the department: a draft for approval, never for everyone (builder §16.2). */
export const DEPARTMENT_DIRECTIVE = "scope: department – make any screen change as a draft for the person's department (ui_patch with scope department), to be approved by a Director who is not the requester; never for everyone";

/** The questions' intro repeats the answer's text: the card then uses its own heading. */
function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  const n = (x: string | null | undefined) => (x ?? "").trim().replace(/[.:،]+$/, "").toLowerCase();
  return !!a && n(a) === n(b);
}

function joinDirectives(...parts: Array<string | null | undefined>): string | undefined {
  const list = parts.map((p) => p?.trim()).filter((p): p is string => !!p);
  return list.length ? list.join("\n") : undefined;
}

// ---------------------------------------------------------------- helpers

/** "Today · 08:22", "Yesterday · 17:40", "Mon 12 Oct · 08:22" (Arabic: «اليوم · 08:22», «الاثنين 12 أكتوبر · 08:22»; Western digits). */
function dayLabel(when: Date, locale: string, t: (key: string, options: { defaultValue: string }) => string): string {
  const ar = locale === "ar";
  const tag = ar ? "ar-AE" : "en-GB";
  const time = new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit", hour12: false, numberingSystem: "latn" }).format(when);
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const day = when.toDateString() === today.toDateString() ? t("assistant:econic.sidebar.day.today", { defaultValue: "Today" })
    : when.toDateString() === yesterday.toDateString() ? t("assistant:econic.sidebar.day.yesterday", { defaultValue: "Yesterday" })
    : new Intl.DateTimeFormat(tag, { weekday: "short", day: "numeric", month: ar ? "long" : "short", numberingSystem: "latn" }).format(when);
  return `${day} · ${time}`;
}

const KNOWN_SOURCES = new Set<CitationLike["sourceType"]>(["Regulation", "Document", "DataBank", "Precedent", "Tool"]);
function normaliseSourceType(s: string): CitationLike["sourceType"] {
  return KNOWN_SOURCES.has(s as CitationLike["sourceType"]) ? (s as CitationLike["sourceType"]) : "Document";
}

function localName(a: AssistantSummary | null, locale: string): string | null {
  if (!a) return null;
  return locale === "ar" && a.nameAr ? a.nameAr : a.name;
}

function starter(p: string | SuggestedPrompt, locale: string): StarterPrompt {
  if (typeof p === "string") return { text: p };
  return { text: (locale === "ar" && p.textAr ? p.textAr : p.text) ?? "", icon: p.icon };
}
